import React from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { AuthProvider, useAuth } from "@/lib/auth";

import Landing from "@/pages/Landing";
import DemoSandbox from "@/pages/DemoSandbox";
import Methodology from "@/pages/Methodology";
import LegalReviewProcess from "@/pages/LegalReviewProcess";
import DataVerification from "@/pages/DataVerification";
import Transparency from "@/pages/Transparency";
import SampleReport from "@/pages/SampleReport";
import Login from "@/pages/Login";
import Register from "@/pages/Register";
import Onboarding from "@/pages/Onboarding";
import Dashboard from "@/pages/Dashboard";
import AuditHistoryDetail from "@/pages/AuditHistoryDetail";
import Remediation from "@/pages/Remediation";
import SharedReport from "@/pages/SharedReport";
import LegalGenerator from "@/pages/LegalGenerator";
import Billing from "@/pages/Billing";
import Admin from "@/pages/Admin";
import AuthCallback from "@/pages/AuthCallback";
import PrivacyPolicy from "@/pages/PrivacyPolicy";
import TermsOfService from "@/pages/TermsOfService";
import RefundPolicy from "@/pages/RefundPolicy";
import FederalScopeNotice from "@/pages/FederalScopeNotice";
import Settings from "@/pages/Settings";
import NotFound from "@/pages/NotFound";
import AdminLogin from "@/pages/AdminLogin";
import Testimonials from "@/pages/Testimonials";
import VerifyEmail from "@/pages/VerifyEmail";
import Changelog from "@/pages/Changelog";
import FAQ from "@/pages/FAQ";
import About from "@/pages/About";
import Contact from "@/pages/Contact";
import Blog from "@/pages/Blog";
import BlogPost from "@/pages/BlogPost";
import TermsAcceptanceModal from "@/components/TermsAcceptanceModal";

const ProtectedRoute = ({ children, adminOnly = false }) => {
  const { user, loading } = useAuth();
  if (loading) return null;
  if (!user) return <Navigate to="/login" replace />;
  if (adminOnly && !user.is_admin) return <Navigate to="/dashboard" replace />;
  return children;
};

function App() {
  return (
    <div className="App">
      <BrowserRouter>
        <AuthProvider>
          <TermsAcceptanceModal />
          <Routes>
            <Route path="/" element={<Landing />} />
            <Route path="/demo" element={<DemoSandbox />} />
            <Route path="/methodology" element={<Methodology />} />
            <Route path="/legal-review-process" element={<LegalReviewProcess />} />
            <Route path="/data-verification" element={<DataVerification />} />
            <Route path="/transparency" element={<Transparency />} />
            <Route path="/sample-report" element={<SampleReport />} />
            <Route path="/privacy-policy" element={<PrivacyPolicy />} />
            <Route path="/terms-of-service" element={<TermsOfService />} />
            <Route path="/refund-policy" element={<RefundPolicy />} />
            <Route path="/federal-scope-notice" element={<FederalScopeNotice />} />

            <Route path="/login" element={<Login />} />
            <Route path="/register" element={<Register />} />
            <Route path="/auth/callback" element={<AuthCallback />} />
            <Route path="/verify-email" element={<VerifyEmail />} />
            <Route path="/changelog" element={<Changelog />} />
            <Route path="/faq" element={<FAQ />} />
            <Route path="/about" element={<About />} />
            <Route path="/contact" element={<Contact />} />
            <Route path="/blog" element={<Blog />} />
            <Route path="/blog/preview/:id" element={<BlogPost preview={true} />} />
            <Route path="/blog/:slug" element={<BlogPost />} />

            <Route path="/onboarding" element={<ProtectedRoute><Onboarding /></ProtectedRoute>} />
            <Route path="/dashboard" element={<ProtectedRoute><Dashboard /></ProtectedRoute>} />
            <Route path="/dashboard/history/:auditId" element={<ProtectedRoute><AuditHistoryDetail /></ProtectedRoute>} />
            <Route path="/settings" element={<ProtectedRoute><Settings /></ProtectedRoute>} />
            <Route path="/remediation" element={<ProtectedRoute><Remediation /></ProtectedRoute>} />
            <Route path="/legal-generator" element={<ProtectedRoute><LegalGenerator /></ProtectedRoute>} />
            <Route path="/billing" element={<Billing />} />
            <Route path="/billing/success" element={<Billing />} />

            <Route path="/report/:reportId" element={<SharedReport />} />

            <Route path="/testimonials" element={<Testimonials />} />

            <Route path="/admin/login" element={<AdminLogin />} />
            <Route path="/admin" element={<Admin />} />
            <Route path="/admin/dashboard" element={<Admin />} />

            <Route path="*" element={<NotFound />} />
          </Routes>
        </AuthProvider>
      </BrowserRouter>
    </div>
  );
}

export default App;
